import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentConfig, Fields } from '@puckeditor/core';
import { ErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { sanitizeUrl } from '@braintree/sanitize-url';

import { resolveAltText } from '../fields/widgets/alt-text-field';
import { booleanField } from '../fields/widgets/boolean-field';
import { selectField } from '../fields/widgets/select-field';
import { slideshowManagerField } from '../fields/widgets/slideshow-manager-field';
import { createSpacingFields } from '../fields/spacing-fields';
import { buildImgixDelivery, encodeSrcSetUrl, isImgixHostedSrc } from '../image-src';
import { IMAGE_ASPECT_RATIOS, IMAGE_RADII, lookup, spacingToCss } from '../fields/tokens';
import type { BoxSpacing, ImageAspectRatio } from '../fields/tokens';
import { interpolateFromPuck } from '../interpolation';
import { imgixSizing } from './image-style';
import { isSafeSrc } from './image';
import { SLIDESHOW_SELECTION_EVENT } from './slideshow-selection';
import type { SlideshowSelectionDetail } from './slideshow-selection';
import type { SlideshowSpecialsResult } from './slideshow-specials';
import { isSlideRecord, slideRowType, slideString } from './slideshow-contract';
import type { Slide } from './slideshow-contract';

/**
 * An **author-built** rotating set of image slides.
 *
 * Deliberately distinct from menu-landing's `Image Banner Rotation`
 * (`menu-landing/blocks/homepage-elements.tsx`), which has `fields: {}` and simply mirrors
 * `dispensary.imageBanners` -- a read-only reflection of what the store configured under
 * Customize, where an author cannot add, remove or reorder anything. That legacy block stays
 * registered for existing content but is hidden from new insertion; Slideshow is the authored path.
 *
 * Image rows still render entirely from authored props. A typed Special row resolves only its live
 * eligibility and destination for the exact rendering store; its imagery remains authored. That
 * narrow optional connection keeps Slideshow in the shared content palette without reviving the
 * legacy `MenuImageBanner` behavior that could retain inactive Specials or guess an offer route.
 */

/**
 * Author-facing label. Deliberately just 'Slideshow' rather than 'Slideshow (your own slides)' --
 * shortened per explicit product request after ECOMM-13234 shipped. The longer form was the only
 * VISIBLE distinction from "Image Banner Rotation" a human scanning the panel by eye would see;
 * that disambiguation now lives ONLY in `ai.instructions` below, which still spells it out in full
 * for AI-assisted editing. A human is expected to tell the two apart by context/preview instead of
 * label text now -- this is a considered tradeoff, not a gap to silently fix by re-lengthening it.
 */
const SLIDESHOW_LABEL = 'Slideshow';

const SlideshowConnectedSpecials = lazy(() => import('./slideshow-connected-specials'));

export type { Slide } from './slideshow-contract';

export type SlideshowProps = {
  slides: Slide[];
  aspectRatio: ImageAspectRatio;
  borderRadius: string;
  autoplay: boolean;
  intervalSeconds: string;
  showArrows: boolean;
  showDots: boolean;
  padding: BoxSpacing;
};

// Stable references. A fresh `[]` or object literal as a destructuring default is a new value on
// every render, which defeats memoisation and makes a derived parity guard compare unequal objects.
const EMPTY_SLIDES: Slide[] = [];
const DEFAULT_PADDING: BoxSpacing = { top: '0px', right: '0px', bottom: '0px', left: '0px' };

const AUTOPLAY_SECONDS = { '3': 3000, '5': 5000, '8': 8000 } as const;

const INTERVAL_OPTIONS = [
  { label: '3 seconds', value: '3' },
  { label: '5 seconds', value: '5' },
  { label: '8 seconds', value: '8' },
] as const;

/** Saved props are unvalidated JSON, regardless of their declared authoring type. */
function booleanOrDefault(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

const ASPECT_RATIO_OPTIONS: { label: string; value: ImageAspectRatio }[] = [
  { label: 'Original', value: 'original' },
  { label: 'Square (1:1)', value: 'square' },
  { label: 'Landscape (4:3)', value: 'landscape' },
  { label: 'Wide (16:9)', value: 'wide' },
];

/**
 * Ceiling on how tall an 'Original' slide may render before `object-fit: cover` (already on
 * `SlideImage`) crops it down to this ratio instead of letterboxing. **PM-tunable: this is the ONE
 * constant to change to move the ceiling.** '3 / 4' means an image may be at most 4:3 taller than it
 * is wide before cropping kicks in; reuses the existing `portrait` token as a sensible default
 * rather than inventing a new number.
 *
 * Exists because, with no ceiling, `Frame`'s height for 'original' was driven purely by whichever
 * slide's own natural proportions happened to be tallest: `Frame`'s `aspect-ratio` was the literal
 * `'auto'`, so its height came from `Track`'s flex-stretch chain reading each `<img>`'s intrinsic
 * size. One unusually tall image anywhere in the set inflated the SHARED frame height for every
 * slide, and `object-fit: cover` never got a ceiling to crop against. Root-caused and requested by
 * the PR author during click-testing of ECOMM-13234 -- follow-up to the original scope, not part of
 * it; see `effectiveOriginalRatio` below for how the ceiling is applied.
 */
const MAX_ORIGINAL_ASPECT_RATIO = IMAGE_ASPECT_RATIOS.portrait; // '3 / 4'

/** Parses this file's own `'<width> / <height>'` token format into a plain width÷height number. */
function parseRatioValue(ratio: string): number {
  const [width, height] = ratio.split('/').map((part) => Number(part.trim()));
  return width / height;
}

const MAX_ORIGINAL_ASPECT_RATIO_VALUE = parseRatioValue(MAX_ORIGINAL_ASPECT_RATIO);

/**
 * Corner-rounding options, deliberately EXCLUDING `circle` and `pill`.
 *
 * Those two are shape choices rather than rounding: `image-style.ts` couples `circle` to a forced
 * square aspect ratio precisely because "an ellipse is not a circle", and a slideshow is a
 * multi-slide strip whose whole point is a shared non-square frame. Offering `circle` here would
 * clip every slide into an oval (`border-radius: 50%` on a wide frame), or else require forcing the
 * frame square, which defeats the block. `pill` has the same problem at a wide aspect ratio.
 *
 * Excluding them at the picker beats coupling them at render: an author never meets a control whose
 * only correct use is a shape they did not ask for.
 */
const EXCLUDED_RADII = new Set(['circle', 'pill']);

/**
 * Author-facing labels, matching the Image block's `RADIUS_OPTIONS` wording for the same tokens.
 *
 * Deriving these from `Object.keys(IMAGE_RADII)` was shorter but put the internal token names in
 * the dropdown -- an author picked "subtle" rather than "Slightly rounded". The keys are still the
 * source of truth for WHICH radii exist: anything unlabelled below falls back to its key, and the
 * filter keeps the excluded shapes out, so adding a radius token cannot silently drop an option.
 */
const RADIUS_LABELS: Record<string, string> = {
  none: 'None',
  subtle: 'Slightly rounded',
  rounded: 'Rounded',
};

export const RADIUS_OPTIONS = Object.keys(IMAGE_RADII)
  .filter((key) => !EXCLUDED_RADII.has(key))
  .map((key) => ({ label: RADIUS_LABELS[key] ?? key, value: key }));

const FrameWrapper = styled.div`
  width: 100%;
  padding: var(--cms-slideshow-padding, 0);
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
  aspect-ratio: var(--cms-slideshow-aspect-ratio, auto);
  border-radius: var(--cms-slideshow-border-radius, 0px);
`;

const Track = styled.div`
  display: flex;
  height: 100%;
  transition: transform 400ms ease;
  transform: translateX(var(--cms-slideshow-offset, 0%));

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SlideItem = styled.div`
  flex: 0 0 100%;
  min-width: 100%;
  height: 100%;
`;

const SlidePicture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;
`;

const SlideImage = styled.img`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 200px;
  border: 1px dashed var(--dcm-cms-placeholder-border-color, #c2c9cd);
  border-radius: var(--dcm-cms-placeholder-radius, 8px);
  background: var(--dcm-cms-image-placeholder-bg, #f0f2f4);
  color: var(--dcm-cms-image-placeholder-color, #646d72);
  font-size: 14px;
`;

const NavButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  background: var(--dcm-cms-slideshow-nav-bg, rgba(255, 255, 255, 0.9));
  color: var(--dcm-cms-slideshow-nav-color, #393c40);
  cursor: pointer;
  line-height: 1;

  &:focus-visible {
    outline: 2px solid var(--dcm-color-primary, #0b99a8);
    outline-offset: 2px;
  }
`;

const PrevButton = styled(NavButton)`
  left: 8px;
`;

const NextButton = styled(NavButton)`
  right: 8px;
`;

const Dots = styled.div`
  position: absolute;
  bottom: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
`;

const RotationButton = styled(NavButton)`
  top: 8px;
  right: 8px;
  transform: none;
  width: auto;
  min-width: 32px;
  padding: 0 8px;
  border-radius: var(--dcm-cms-slideshow-rotation-radius, 16px);
  font-size: 12px;
`;

const VisuallyHiddenStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Dot = styled.button<{ isActive: boolean }>`
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  background: ${({ isActive }) =>
    isActive ? 'var(--dcm-cms-slideshow-dot-active, #393c40)' : 'var(--dcm-cms-slideshow-dot, rgba(0,0,0,0.3))'};

  &:focus-visible {
    outline: 2px solid var(--dcm-color-primary, #0b99a8);
    outline-offset: 2px;
  }
`;

/**
 * Resolves the frame's corner rounding, refusing the shape-changing radii.
 *
 * The picker no longer offers `circle` or `pill`, but saved page data is unvalidated JSON that a
 * hand edit or an AI edit can author, and the published renderer has no error boundary -- a stored
 * `circle` would clip every slide into an oval rather than failing visibly.
 */
export function resolveSlideshowRadius(borderRadius: string): string {
  if (EXCLUDED_RADII.has(borderRadius)) {
    return IMAGE_RADII.none;
  }
  return lookup(IMAGE_RADII, borderRadius, borderRadius);
}

/**
 * Keeps only slides an author has actually finished, in the order they arranged them.
 *
 * Saved page data is unvalidated JSON, so `slides` may be a non-array, and an individual slide may
 * be missing its image because the author added a row and has not picked one yet. The published
 * renderer has no error boundary, so neither may throw. Order is preserved exactly: the array's
 * order IS the author's decision, expressed through the dedicated Slide Manager.
 */
type RenderableSlide = {
  src: string;
  mobileSrc: string;
  alt: ReturnType<typeof resolveAltText>;
  linkHref: string;
  linkName: string;
  identity: string;
};

const EMPTY_RENDERABLE_SLIDES: RenderableSlide[] = [];

export function usableSlides(slides: unknown, specialState?: SlideshowSpecialsResult): RenderableSlide[] {
  if (!Array.isArray(slides)) {
    return EMPTY_RENDERABLE_SLIDES;
  }
  return slides.reduce<RenderableSlide[]>((acc, slide) => {
    const type = slideRowType(slide);
    if (type !== 'image' && type !== 'special') return acc;

    const src = slideString(slide, 'src');
    if (!src || !isSafeSrc(src)) return acc;

    let linkHref = slideString(slide, 'linkHref');
    let linkName = '';
    const persistedKind = slideString(slide, 'kind');
    let imageKind = 'legacy-image';
    if (persistedKind) imageKind = persistedKind;
    let identity = `${imageKind}:${src}`;
    if (type === 'special') {
      const specialId = slideString(slide, 'specialId');
      const reference = specialState?.status === 'ready' ? specialState.references[specialId] : undefined;
      if (!reference?.destination) return acc;
      linkHref = reference.destination;
      linkName = reference.name;
      identity = `special:${specialId}`;
    }

    const mobileSrc = slideString(slide, 'mobileSrc');
    acc.push({
      src,
      mobileSrc: mobileSrc && isSafeSrc(mobileSrc) ? mobileSrc : '',
      alt: resolveAltText(isSlideRecord(slide) ? slide.alt : undefined),
      linkHref,
      linkName,
      identity,
    });
    return acc;
  }, []);
}

type SlideshowRenderProps = SlideshowProps & {
  id: string;
  puck: { isEditing: boolean; metadata?: Record<string, unknown> };
};

function SlideshowResolvedRender({
  id,
  slides = EMPTY_SLIDES,
  aspectRatio = 'original',
  borderRadius = 'none',
  autoplay = false,
  intervalSeconds = '5',
  showArrows = true,
  showDots = true,
  padding = DEFAULT_PADDING,
  puck,
  specialState,
}: SlideshowRenderProps & { specialState?: SlideshowSpecialsResult }): JSX.Element {
  const visible = usableSlides(slides, specialState);
  const [index, setIndex] = useState(0);
  const [rotationPaused, setRotationPaused] = useState(false);
  const [announceChanges, setAnnounceChanges] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [reducedMotionOverride, setReducedMotionOverride] = useState(false);
  const rotationPointerDownRef = useRef(false);
  const count = visible.length;

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = (): void => setPrefersReducedMotion(query.matches);
    update();
    const changeTarget = (query as unknown) as {
      addEventListener?: (type: string, listener: () => void) => void;
      removeEventListener?: (type: string, listener: () => void) => void;
      addListener?: (listener: () => void) => void;
      removeListener?: (listener: () => void) => void;
    };
    if (changeTarget.addEventListener && changeTarget.removeEventListener) {
      changeTarget.addEventListener('change', update);
      return () => changeTarget.removeEventListener?.('change', update);
    }
    changeTarget.addListener?.(update);
    return () => changeTarget.removeListener?.(update);
  }, []);

  useEffect(() => {
    if (!puck.isEditing || typeof window === 'undefined') return undefined;
    const handleSelection = (event: Event): void => {
      const { detail } = event as CustomEvent<SlideshowSelectionDetail>;
      if (detail.componentId === id) setIndex(detail.index);
    };
    window.addEventListener(SLIDESHOW_SELECTION_EVENT, handleSelection);
    return () => window.removeEventListener(SLIDESHOW_SELECTION_EVENT, handleSelection);
  }, [id, puck.isEditing]);

  // Each loaded slide's REAL natural width÷height ratio, keyed by `src` rather than index so
  // reordering the author's slides can never desync a stale ratio onto a different slide. Read only
  // by `effectiveOriginalRatio` below, for the 'original' shape -- fixed shapes never consult this
  // and are unaffected by it existing.
  const [naturalRatios, setNaturalRatios] = useState<Record<string, number>>({});

  const handleSlideImageLoad = useCallback((src: string, event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.naturalWidth <= 0 || img.naturalHeight <= 0) {
      return;
    }
    const ratio = img.naturalWidth / img.naturalHeight;
    setNaturalRatios((prev) => (prev[src] === ratio ? prev : { ...prev, [src]: ratio }));
  }, []);

  /**
   * The 'original' shape's aspect-ratio, computed rather than looked up literally the way every
   * fixed shape is -- see MAX_ORIGINAL_ASPECT_RATIO above for why 'original' needs a ceiling at all.
   *
   * Before any image in the set has reported its size, this stays `IMAGE_ASPECT_RATIOS.original`
   * ('auto'), exactly as today: no first-paint flash of a zero-height frame while images are still
   * fetching.
   *
   * Once at least one has loaded, the frame's ratio is the TALLEST natural ratio known across all
   * currently-loaded slides (smallest width÷height value) -- the shared-frame-height architecture
   * is unchanged, every mode has always sized the frame off every slide, not just the visible one --
   * floored at MAX_ORIGINAL_ASPECT_RATIO_VALUE so nothing can drive the frame taller than the cap
   * allows. `Math.max` only ever RAISES the value (makes the frame shorter/wider), so a normal
   * slide that never approaches the ceiling keeps its own true ratio untouched -- 'Original' still
   * means "true to source" for anything that does not need cropping.
   */
  const effectiveOriginalRatio = useMemo(() => {
    const loadedRatios = visible
      .map((slide) => naturalRatios[slide.src])
      .filter((ratio): ratio is number => typeof ratio === 'number');
    if (loadedRatios.length === 0) {
      return IMAGE_ASPECT_RATIOS.original;
    }
    const tallest = Math.min(...loadedRatios);
    return String(Math.max(tallest, MAX_ORIGINAL_ASPECT_RATIO_VALUE));
  }, [visible, naturalRatios]);

  // An author removing slides can leave the index past the end.
  const safeIndex = count === 0 ? 0 : Math.min(index, count - 1);

  const goTo = useCallback((next: number) => {
    setAnnounceChanges(true);
    setIndex(next);
  }, []);
  const goPrev = useCallback(() => {
    setAnnounceChanges(true);
    setIndex((i) => (count === 0 ? 0 : (i - 1 + count) % count));
  }, [count]);
  const goNext = useCallback(() => {
    setAnnounceChanges(true);
    setIndex((i) => (count === 0 ? 0 : (i + 1) % count));
  }, [count]);

  const { isEditing } = puck;
  const rotationStopped = rotationPaused || (prefersReducedMotion && !reducedMotionOverride);
  const autoplayEnabled = booleanOrDefault(autoplay, false) && count > 1 && !isEditing && !rotationStopped;
  const arrowsEnabled = booleanOrDefault(showArrows, true);
  const dotsEnabled = booleanOrDefault(showDots, true);
  const intervalMs = lookup(AUTOPLAY_SECONDS, intervalSeconds, AUTOPLAY_SECONDS['5']);
  const autoAdvance = useCallback(() => {
    setAnnounceChanges(false);
    setIndex((i) => (count === 0 ? 0 : (i + 1) % count));
  }, [count]);
  const goNextRef = useRef(autoAdvance);
  goNextRef.current = autoAdvance;

  useEffect(() => {
    // Autoplay is suppressed in the editor: a slideshow advancing under the author while they are
    // editing a specific slide makes the panel and the canvas disagree about what they are looking
    // at. It is also suppressed for a single slide, where it would only cause needless re-renders.
    if (!autoplayEnabled) {
      return undefined;
    }
    const timer = setInterval(() => goNextRef.current(), intervalMs);
    return () => clearInterval(timer);
  }, [autoplayEnabled, intervalMs]);

  if (count === 0) {
    // In production an unconfigured block renders nothing rather than an empty frame shoppers can
    // see; in the editor it must stay visible and selectable, or Puck's bounding-rect hit test
    // makes it impossible to click or drag.
    if (!isEditing) {
      return <></>;
    }
    const hasConnectedSlides = Array.isArray(slides) && slides.some((slide) => slideRowType(slide) === 'special');
    if (hasConnectedSlides) {
      if (specialState?.status === 'loading') {
        return <Placeholder>Checking connected Special availability for this preview store.</Placeholder>;
      }
      if (specialState?.status === 'error') {
        return <Placeholder>Special availability could not be loaded. The authored row is preserved.</Placeholder>;
      }
      if (specialState?.status === 'missing-store') {
        return <Placeholder>Choose an exact preview store to check the connected Special.</Placeholder>;
      }
      return <Placeholder>The connected Special is unavailable for this preview store.</Placeholder>;
    }
    return <Placeholder>Add at least one slide with an image in the settings panel.</Placeholder>;
  }

  return (
    <FrameWrapper style={{ '--cms-slideshow-padding': spacingToCss(padding) } as React.CSSProperties}>
      <Frame
        data-testid='cms-slideshow'
        role='region'
        aria-roledescription='carousel'
        aria-label='Slideshow'
        onMouseEnter={() => setRotationPaused(true)}
        onFocusCapture={() => {
          if (!rotationPointerDownRef.current) setRotationPaused(true);
        }}
        style={
          {
            '--cms-slideshow-aspect-ratio':
              aspectRatio === 'original'
                ? effectiveOriginalRatio
                : lookup(IMAGE_ASPECT_RATIOS, aspectRatio, IMAGE_ASPECT_RATIOS.original),
            '--cms-slideshow-border-radius': resolveSlideshowRadius(borderRadius),
            '--cms-slideshow-offset': `-${safeIndex * 100}%`,
          } as React.CSSProperties
        }
      >
        {booleanOrDefault(autoplay, false) && count > 1 && !isEditing && (
          <RotationButton
            type='button'
            data-rotation-control='true'
            onPointerDown={() => {
              rotationPointerDownRef.current = true;
            }}
            onPointerUp={() => {
              rotationPointerDownRef.current = false;
            }}
            onPointerCancel={() => {
              rotationPointerDownRef.current = false;
            }}
            onBlur={() => {
              rotationPointerDownRef.current = false;
            }}
            onClick={(event) => {
              const shouldStart = event.currentTarget.getAttribute('aria-label') === 'Start slide rotation';
              if (shouldStart) setReducedMotionOverride(true);
              setRotationPaused(!shouldStart);
            }}
            aria-label={rotationStopped ? 'Start slide rotation' : 'Pause slide rotation'}
          >
            {rotationStopped ? 'Play' : 'Pause'}
          </RotationButton>
        )}

        <Track data-testid='cms-slideshow-track' data-order={visible.map((slide) => slide.src).join(',')}>
          {visible.map((slide, slideIndex) => {
            const delivery = isImgixHostedSrc(slide.src)
              ? buildImgixDelivery(slide.src, imgixSizing('100%'))
              : undefined;
            const mobileDelivery =
              slide.mobileSrc && isImgixHostedSrc(slide.mobileSrc)
                ? buildImgixDelivery(slide.mobileSrc, imgixSizing('100%'))
                : undefined;
            const resolved = resolveAltText(slide.alt);
            const interpolatedHref = interpolateFromPuck(slide.linkHref, puck).trim();
            const sanitizedHref = interpolatedHref ? sanitizeUrl(interpolatedHref) : '';
            const safeHref = sanitizedHref === 'about:blank' ? '' : sanitizedHref;
            const image = (
              <SlidePicture>
                {slide.mobileSrc && (
                  <source
                    media='(max-width: 768px)'
                    srcSet={mobileDelivery?.srcSet ?? mobileDelivery?.src ?? encodeSrcSetUrl(slide.mobileSrc)}
                    sizes={mobileDelivery?.sizes}
                  />
                )}
                <SlideImage
                  src={delivery?.src ?? slide.src}
                  srcSet={delivery?.srcSet}
                  sizes={delivery?.sizes}
                  alt={resolved.decorative ? '' : resolved.alt}
                  aria-hidden={resolved.decorative ? true : undefined}
                  loading={slideIndex === 0 ? 'eager' : 'lazy'}
                  onLoad={(event) => handleSlideImageLoad(slide.src, event)}
                />
              </SlidePicture>
            );
            return (
              <SlideItem
                // eslint-disable-next-line react/no-array-index-key -- duplicate authored images are valid
                key={`${slide.identity}-${slideIndex}`}
                data-testid='cms-slideshow-slide'
                role='group'
                aria-roledescription='slide'
                aria-label={`${slideIndex + 1} of ${count}`}
                aria-hidden={slideIndex === safeIndex ? undefined : true}
              >
                {safeHref ? (
                  <a
                    href={safeHref}
                    tabIndex={slideIndex === safeIndex ? undefined : -1}
                    aria-label={resolved.decorative ? `Open ${slide.linkName || safeHref}` : undefined}
                  >
                    {image}
                  </a>
                ) : (
                  image
                )}
              </SlideItem>
            );
          })}
        </Track>

        <VisuallyHiddenStatus aria-live={announceChanges ? 'polite' : 'off'} aria-atomic='true'>
          {`Slide ${safeIndex + 1} of ${count}`}
        </VisuallyHiddenStatus>

        {arrowsEnabled && count > 1 && (
          <>
            <PrevButton type='button' onClick={goPrev} aria-label='Previous slide' data-testid='cms-slideshow-prev'>
              ‹
            </PrevButton>
            <NextButton type='button' onClick={goNext} aria-label='Next slide' data-testid='cms-slideshow-next'>
              ›
            </NextButton>
          </>
        )}

        {dotsEnabled && count > 1 && (
          <Dots role='group' aria-label='Choose slide'>
            {visible.map((slide, dotIndex) => (
              <Dot
                // eslint-disable-next-line react/no-array-index-key -- duplicate authored images are valid
                key={`dot-${slide.identity}-${dotIndex}`}
                type='button'
                isActive={dotIndex === safeIndex}
                aria-current={dotIndex === safeIndex ? 'true' : undefined}
                aria-label={`Go to slide ${dotIndex + 1}`}
                data-testid='cms-slideshow-dot'
                onClick={() => goTo(dotIndex)}
              />
            ))}
          </Dots>
        )}
      </Frame>
    </FrameWrapper>
  );
}

function SlideshowRender(props: SlideshowRenderProps): JSX.Element {
  const { slides: storedSlides, puck } = props;
  const slides = Array.isArray(storedSlides) ? storedSlides : EMPTY_SLIDES;
  const specialIds = slides.reduce<string[]>((ids, slide) => {
    if (slideRowType(slide) === 'special') ids.push(slideString(slide, 'specialId'));
    return ids;
  }, []);

  if (specialIds.length === 0) return <SlideshowResolvedRender {...props} />;

  const imageOnlyFallback = <SlideshowResolvedRender {...props} />;

  return (
    <ErrorBoundary fallback={imageOnlyFallback} resetKeys={[specialIds.join(',')]}>
      <Suspense
        fallback={
          puck.isEditing ? (
            <Placeholder>Checking connected Special availability for this preview store.</Placeholder>
          ) : (
            imageOnlyFallback
          )
        }
      >
        <SlideshowConnectedSpecials specialIds={specialIds}>
          {(specialState) => <SlideshowResolvedRender {...props} specialState={specialState} />}
        </SlideshowConnectedSpecials>
      </Suspense>
    </ErrorBoundary>
  );
}

export const slideshowConfig: ComponentConfig<SlideshowProps> = {
  label: SLIDESHOW_LABEL,
  ai: {
    instructions:
      'A rotating set of image slides that the AUTHOR builds and orders here. Not the same as "Image Banner Rotation", which only replays the image banners the store already configured under Customize and cannot be edited from a page.',
  },
  fields: {
    slides: slideshowManagerField,
    aspectRatio: selectField(
      'Shape',
      ASPECT_RATIO_OPTIONS,
      'The frame every slide is cropped to. Use Wide for a banner-like strip across the page.',
      'All slides share one shape, so mixed-size images stay aligned.'
    ),
    borderRadius: selectField('Corner rounding', RADIUS_OPTIONS),
    autoplay: booleanField(
      'Advance automatically',
      'Set to true to rotate slides on a timer. Readers can always use the arrows and dots regardless.',
      'Paused while you are editing, so the canvas does not move under you.'
    ),
    intervalSeconds: selectField('Seconds per slide', INTERVAL_OPTIONS),
    showArrows: booleanField('Show arrows'),
    showDots: booleanField('Show dots'),
    padding: {
      type: 'object',
      label: 'Spacing',
      objectFields: createSpacingFields(),
    },
  },
  // `<Render>` does not merge defaultProps, so every destructuring default in `SlideshowRender`
  // must stay byte-identical to its value here.
  defaultProps: {
    slides: EMPTY_SLIDES,
    aspectRatio: 'original',
    borderRadius: 'none',
    autoplay: false,
    intervalSeconds: '5',
    showArrows: true,
    showDots: true,
    padding: DEFAULT_PADDING,
  },
  resolveFields: (data, { fields }) => {
    // Seconds per slide only means something while the slideshow advances on its own, so the panel
    // does not offer a control with no effect.
    // Cast: TypeScript requires the full Fields<SlideshowProps> shape, but Puck treats the returned
    // set as the complete panel field list, so a control can legitimately be absent.
    const { intervalSeconds: intervalField, ...baseFields } = fields;
    return (booleanOrDefault(data.props.autoplay, false)
      ? { ...baseFields, intervalSeconds: intervalField }
      : baseFields) as Fields<SlideshowProps>;
  },
  render: SlideshowRender,
};
